/**
 * ViewConfigModal.js
 *
 * A "Configure View" modal for the Call List, letting the user reorder
 * Columns, Flags, and Tags. Each pill has the handle on the left and
 * an 'x' on the right for removal.
 */

import React, { useState, useEffect } from 'react';
import { Radio, Modal, Tabs, Button, Space, Input, Select,   InputNumber,
    Typography,
    Alert } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { TabPane } = Tabs;
const { Text } = Typography;

/**
 * DraggablePill: a pill with the drag-handle on the LEFT ("≡")
 * and an "x" remove button on the RIGHT
 */
function DraggablePill({ item, activeTab, onSelect, onRemove, dragId }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: dragId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 9999 : 1,
        margin: '4px',
        display: 'inline-block',
        position: 'relative',
    };

    // Basic pill style: green background, white text
    // (Flags or tags might override if you want.)
    const pillStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        backgroundColor: '#10b981',
        color: '#fff',
        borderRadius: '16px',
        cursor: 'pointer',
        userSelect: 'none',
        fontSize: '14px',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div style={pillStyle}>
                {/* DRAG HANDLE on LEFT */}
                <span {...listeners} style={{ cursor: 'grab' }}>
          ≡
        </span>

                {/* PILL LABEL => onSelect if clicked in center */}
                <span
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelect(item);
                    }}
                >
          {item.label}
        </span>

                {/* "x" on the RIGHT => Remove */}
                <span
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(item);
                    }}
                    style={{ marginLeft: '8px' }}
                >
          x
        </span>
            </div>
        </div>
    );
}

/**
 * getExpressionPlaceholder: a little helper for placeholders in the Expression field
 */
function getExpressionPlaceholder(item, activeTab) {
    if (!item || !item.field) return 'Enter expression';
    if (activeTab === 'columns' || activeTab === 'tags') {
        return `e.g. record.${item.field}.toUpperCase()`;
    }
    return 'Enter expression';
}

/**
 * A mini config form for each item in columns, flags, or tags
 * The shape differs slightly:
 *  - columns: expression?
 *  - flags: condition?
 *  - tags: expression?
 */
function ItemConfigForm({ item, activeTab, onUpdate }) {
    if (!item) return null;

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Input
                addonBefore="Label"
                value={item.label}
                onChange={(e) => onUpdate({ ...item, label: e.target.value })}
            />

            {/* For columns and tags, we can have an optional "expression" field. */}
            {activeTab === 'columns' || activeTab === 'tags' ? (
                <Input
                    addonBefore="Expression"
                    placeholder={getExpressionPlaceholder(item, activeTab)}
                    value={item.expression || ''}
                    onChange={(e) => onUpdate({ ...item, expression: e.target.value })}
                />
            ) : null}

            {/* For flags, we have condition & style */}
            {activeTab === 'flags' && (
                <>
                    <Input
                        addonBefore="Condition"
                        value={item.condition}
                        onChange={(e) => onUpdate({ ...item, condition: e.target.value })}
                    />
                    <Space>
                        <span>Background:</span>
                        <Input
                            type="color"
                            value={item.style?.backgroundColor || '#fef3c7'}
                            onChange={(e) =>
                                onUpdate({
                                    ...item,
                                    style: {
                                        ...(item.style || {}),
                                        backgroundColor: e.target.value,
                                    },
                                })
                            }
                            style={{ width: 50, padding: 0 }}
                        />
                        <span>Text:</span>
                        <Input
                            type="color"
                            value={item.style?.color || '#92400e'}
                            onChange={(e) =>
                                onUpdate({
                                    ...item,
                                    style: {
                                        ...(item.style || {}),
                                        color: e.target.value,
                                    },
                                })
                            }
                            style={{ width: 50, padding: 0 }}
                        />
                    </Space>
                </>
            )}

            {/* For tags, we also allow style. */}
            {activeTab === 'tags' && (
                <Space>
                    <span>Background:</span>
                    <Input
                        type="color"
                        value={item.style?.backgroundColor || '#f3f4f6'}
                        onChange={(e) =>
                            onUpdate({
                                ...item,
                                style: {
                                    ...(item.style || {}),
                                    backgroundColor: e.target.value,
                                },
                            })
                        }
                        style={{ width: 50, padding: 0 }}
                    />
                    <span>Text:</span>
                    <Input
                        type="color"
                        value={item.style?.color || '#6b7280'}
                        onChange={(e) =>
                            onUpdate({
                                ...item,
                                style: {
                                    ...(item.style || {}),
                                    color: e.target.value,
                                },
                            })
                        }
                        style={{ width: 50, padding: 0 }}
                    />
                </Space>
            )}
        </Space>
    );
}

/**
 * ViewConfigModal: for "Columns", "Flags", or "Tags" in the call list.
 */
export default function ViewConfigModal({
                                            visible,
                                            onClose,
                                            config,
                                            onConfigChange,
                                            fieldConfig, // e.g. array of {id, title, ...}
                                        }) {
    // We'll track the active tab: "columns", "flags", or "tags"
    const [activeTab, setActiveTab] = useState('columns');

    // We store a local copy of the config so user can revert if they cancel
    const [localConfig, setLocalConfig] = useState(config);
    // The item currently being edited (columns, flags, or tags).
    const [selectedItem, setSelectedItem] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [accentColorConfig, setAccentColorConfig] = useState({
        type: 'metric', // 'metric', 'conditions', or 'expression'
        metric: {
            field: '',
            expression: '',
            maxValue: 100,
            useAutoMax: true
        },
        conditions: [
            { condition: '', color: '#10b981' } // Default green color
        ],
        defaultColor: '#e5e7eb', // Default gray
        expression: ''
    });

    useEffect(() => {
        if (visible) {
            setLocalConfig(config);
            setActiveTab('columns');
            setSelectedItem(null);
            setIsAdding(false);
            if (config.accentColor) {
                setAccentColorConfig(config.accentColor);
            }
        }
    }, [config, visible]);

    // If user changes or cancels, we revert to old config
    const handleCancel = () => {
        setLocalConfig(config);
        onClose();
    };

    const handleApply = () => {
        // Save localConfig to parent
        onConfigChange(localConfig);
        onClose();
    };

    const handleAccentColorChange = (newConfig) => {
        setAccentColorConfig(newConfig);
        setLocalConfig(prev => ({
            ...prev,
            accentColor: newConfig
        }));
    };

    // A helper to reorder items in localConfig[activeTab] when the user drags
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) return;
        if (active.id !== over.id) {
            const items = localConfig[activeTab] || [];
            const oldIndex = items.findIndex((_, i) => `${activeTab}-${i}` === active.id);
            const newIndex = items.findIndex((_, i) => `${activeTab}-${i}` === over.id);
            if (oldIndex < 0 || newIndex < 0) return;
            const newItems = arrayMove(items, oldIndex, newIndex);
            setLocalConfig((prev) => ({
                ...prev,
                [activeTab]: newItems,
            }));
        }
    };

    const renderAccentColorTab = () => {
        return (
            <div style={{ padding: 16 }}>
                <Radio.Group
                    value={accentColorConfig.type}
                    onChange={e => handleAccentColorChange({
                        ...accentColorConfig,
                        type: e.target.value
                    })}
                    style={{ marginBottom: 16 }}
                >
                    <Radio.Button value="metric">Metric-based</Radio.Button>
                    <Radio.Button value="conditions">Conditions</Radio.Button>
                    <Radio.Button value="expression">Expression</Radio.Button>
                </Radio.Group>

                {accentColorConfig.type === 'metric' && renderMetricBasedConfig()}
                {accentColorConfig.type === 'conditions' && renderConditionsBasedConfig()}
                {accentColorConfig.type === 'expression' && renderExpressionBasedConfig()}
            </div>
        );
    };

    // Render metric-based configuration
    const renderMetricBasedConfig = () => {
        return (
            <Space direction="vertical" style={{ width: '100%' }}>
                <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Select field or metric"
                    value={accentColorConfig.metric.field}
                    onChange={value => handleAccentColorChange({
                        ...accentColorConfig,
                        metric: {
                            ...accentColorConfig.metric,
                            field: value
                        }
                    })}
                    options={fieldConfig
                        .filter(f => f.type === 'number')
                        .map(f => ({ label: f.title, value: f.id }))
                    }
                />

                <Input
                    addonBefore="Expression"
                    placeholder="Optional expression e.g. record.qualScore * 0.5"
                    value={accentColorConfig.metric.expression}
                    onChange={e => handleAccentColorChange({
                        ...accentColorConfig,
                        metric: {
                            ...accentColorConfig.metric,
                            expression: e.target.value
                        }
                    })}
                />

                <div style={{ marginTop: 16 }}>
                    <Text strong>Max Value (for percentage calculation)</Text>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <Radio.Group
                            value={accentColorConfig.metric.useAutoMax ? 'auto' : 'custom'}
                            onChange={e => handleAccentColorChange({
                                ...accentColorConfig,
                                metric: {
                                    ...accentColorConfig.metric,
                                    useAutoMax: e.target.value === 'auto'
                                }
                            })}
                        >
                            <Radio value="auto">Auto</Radio>
                            <Radio value="custom">Custom</Radio>
                        </Radio.Group>

                        {!accentColorConfig.metric.useAutoMax && (
                            <InputNumber
                                min={1}
                                value={accentColorConfig.metric.maxValue}
                                onChange={value => handleAccentColorChange({
                                    ...accentColorConfig,
                                    metric: {
                                        ...accentColorConfig.metric,
                                        maxValue: value
                                    }
                                })}
                                style={{ width: 100 }}
                            />
                        )}
                    </div>
                </div>

                <div style={{ marginTop: 16 }}>
                    <Text strong>Preview</Text>
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{
                                width: 20,
                                height: 20,
                                backgroundColor: '#ef4444',
                                borderRadius: 4
                            }}></div>
                            <Text>Below 65%</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{
                                width: 20,
                                height: 20,
                                backgroundColor: '#f97316',
                                borderRadius: 4
                            }}></div>
                            <Text>65% - 80%</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{
                                width: 20,
                                height: 20,
                                backgroundColor: '#22c55e',
                                borderRadius: 4
                            }}></div>
                            <Text>Above 80%</Text>
                        </div>
                    </div>
                </div>
            </Space>
        );
    };

    // Render conditions-based configuration
    const renderConditionsBasedConfig = () => {
        return (
            <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>Conditions</Text>
                <Text type="secondary">
                    Conditions are evaluated in order. The first condition that evaluates to true will determine the color.
                </Text>

                <div style={{ marginTop: 12 }}>
                    {accentColorConfig.conditions.map((condition, index) => (
                        <div
                            key={index}
                            style={{
                                display: 'flex',
                                gap: 8,
                                marginBottom: 12,
                                alignItems: 'flex-start'
                            }}
                        >
                            <Input
                                placeholder="Condition e.g. record.qualScore > 80"
                                value={condition.condition}
                                onChange={e => {
                                    const newConditions = [...accentColorConfig.conditions];
                                    newConditions[index] = {
                                        ...newConditions[index],
                                        condition: e.target.value
                                    };
                                    handleAccentColorChange({
                                        ...accentColorConfig,
                                        conditions: newConditions
                                    });
                                }}
                                style={{ flex: 1 }}
                            />
                            <Input
                                type="color"
                                value={condition.color}
                                onChange={e => {
                                    const newConditions = [...accentColorConfig.conditions];
                                    newConditions[index] = {
                                        ...newConditions[index],
                                        color: e.target.value
                                    };
                                    handleAccentColorChange({
                                        ...accentColorConfig,
                                        conditions: newConditions
                                    });
                                }}
                                style={{ width: 50, padding: 0 }}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                    const newConditions = accentColorConfig.conditions.filter((_, i) => i !== index);
                                    handleAccentColorChange({
                                        ...accentColorConfig,
                                        conditions: newConditions
                                    });
                                }}
                                style={{ padding: '0 8px' }}
                                disabled={accentColorConfig.conditions.length === 1}
                            />
                        </div>
                    ))}

                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            handleAccentColorChange({
                                ...accentColorConfig,
                                conditions: [
                                    ...accentColorConfig.conditions,
                                    { condition: '', color: '#10b981' }
                                ]
                            });
                        }}
                        style={{ width: '100%' }}
                    >
                        Add Condition
                    </Button>
                </div>

                <div style={{ marginTop: 16 }}>
                    <Text strong>Default Color</Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        <span>If no conditions match:</span>
                        <Input
                            type="color"
                            value={accentColorConfig.defaultColor}
                            onChange={e => handleAccentColorChange({
                                ...accentColorConfig,
                                defaultColor: e.target.value
                            })}
                            style={{ width: 50, padding: 0 }}
                        />
                    </div>
                </div>
            </Space>
        );
    };

    // Render expression-based configuration
    const renderExpressionBasedConfig = () => {
        return (
            <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>Color Expression</Text>
                <Text type="secondary">
                    Enter a JavaScript expression that evaluates to a valid CSS color (hex, rgb, etc.)
                </Text>

                <Input.TextArea
                    placeholder="e.g. record.qualScore > 80 ? '#22c55e' : record.qualScore > 65 ? '#f97316' : '#ef4444'"
                    value={accentColorConfig.expression}
                    onChange={e => handleAccentColorChange({
                        ...accentColorConfig,
                        expression: e.target.value
                    })}
                    autoSize={{ minRows: 4, maxRows: 8 }}
                />

                <Alert
                    type="info"
                    showIcon
                    message="Expression should return a valid CSS color"
                    description="Examples: '#ff0000', 'rgb(255, 0, 0)', 'red'"
                />
            </Space>
        );
    };

    const renderPills = () => {
        const items = Array.isArray(localConfig[activeTab])
            ? localConfig[activeTab]
            : [];

        return (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                    items={items.map((_, i) => `${activeTab}-${i}`)}
                    strategy={verticalListSortingStrategy}
                >
                    {items.map((item, index) => {
                        const dragId = `${activeTab}-${index}`;
                        return (
                            <DraggablePill
                                key={dragId}
                                dragId={dragId}
                                item={item}
                                activeTab={activeTab}
                                onSelect={(itm) => setSelectedItem(itm)}
                                onRemove={(itm) => {
                                    const updated = items.filter((x) => x !== itm);
                                    setLocalConfig((prev) => ({
                                        ...prev,
                                        [activeTab]: updated,
                                    }));
                                    if (selectedItem === itm) {
                                        setSelectedItem(null);
                                    }
                                }}
                            />
                        );
                    })}
                    {isAdding ? (
                        <Select
                            autoFocus
                            showSearch
                            open
                            style={{ width: 220, marginLeft: 4 }}
                            placeholder={`Select a field for ${activeTab.slice(0, -1)}`}
                            onBlur={() => setIsAdding(false)}
                            onChange={(val) => {
                                const newField = fieldConfig.find((f) => f.id === val) || { id: val, title: val };
                                const newItem = makeDefaultItem(newField, activeTab);
                                setLocalConfig((prev) => ({
                                    ...prev,
                                    [activeTab]: [...(prev[activeTab] || []), newItem],
                                }));
                                setSelectedItem(newItem);
                                setIsAdding(false);
                            }}
                            options={fieldConfig
                                .filter(
                                    (f) => !(localConfig[activeTab] || []).some((x) => x.field === f.id)
                                )
                                .map((f) => ({
                                    label: f.title,
                                    value: f.id,
                                }))}
                        />
                    ) : (
                        <Button
                            type="link"
                            icon={<PlusOutlined />}
                            style={{ marginLeft: 4 }}
                            onClick={() => setIsAdding(true)}
                        >
                            Add {activeTab.slice(0, -1)}
                        </Button>
                    )}
                </SortableContext>
            </DndContext>
        );
    };

    // The selected item config UI
    const renderItemConfig = () => {
        if (!selectedItem) return null;
        return (
            <div
                style={{
                    marginTop: 16,
                    padding: 16,
                    border: '1px solid #f0f0f0',
                    borderRadius: 4,
                }}
            >
                <ItemConfigForm
                    item={selectedItem}
                    activeTab={activeTab}
                    onUpdate={(updates) => {
                        // update in localConfig
                        setSelectedItem(updates);
                        setLocalConfig((prev) => {
                            const arr = prev[activeTab] || [];
                            const newArr = arr.map((x) => (x === selectedItem ? updates : x));
                            return { ...prev, [activeTab]: newArr };
                        });
                    }}
                />
                <Button
                    type="link"
                    danger
                    onClick={() => {
                        // remove this item
                        const updated = (localConfig[activeTab] || []).filter((x) => x !== selectedItem);
                        setLocalConfig({ ...localConfig, [activeTab]: updated });
                        setSelectedItem(null);
                    }}
                    style={{ marginTop: 8 }}
                >
                    Remove {activeTab.slice(0, -1)}
                </Button>
            </div>
        );
    };
    //console.log("Rendering ViewConfigModal");
    return (
        <Modal
            title="Configure View"
            visible={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="apply" type="primary" onClick={handleApply}>
                    Apply
                </Button>,
            ]}
            width={800}
        >
            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    setActiveTab(key);
                    setSelectedItem(null);
                    setIsAdding(false);
                }}
            >
                <TabPane tab="Columns" key="columns">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {renderPills()}
                    </div>
                    {renderItemConfig()}
                </TabPane>
                <TabPane tab="Flags" key="flags">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {renderPills()}
                    </div>
                    {renderItemConfig()}
                </TabPane>
                <TabPane tab="Tags" key="tags">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {renderPills()}
                    </div>
                    {renderItemConfig()}
                </TabPane>
                <TabPane tab="Accent Color" key="accentColor">
                    {renderAccentColorTab()}
                </TabPane>
            </Tabs>
        </Modal>
    );
}

/** makeDefaultItem: create a default object for columns, flags, or tags. */
function makeDefaultItem(field, activeTab) {
    switch (activeTab) {
        case 'columns':
            return {
                id: field.id,
                field: field.id,
                label: field.title,
                expression: '',
            };
        case 'flags':
            return {
                id: field.id,
                field: field.id,
                label: field.title,
                condition: `record.${field.id} === true`,
                style: {
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                },
            };
        case 'tags':
            return {
                id: field.id,
                field: field.id,
                label: field.title,
                expression: '',
                style: {
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                },
            };
        default:
            return {
                id: field.id,
                field: field.id,
                label: field.title,
            };
    }
}
